import styled from "@emotion/styled";
import type { ComponentType, JSX } from "react";
import ReactMarkdown from "react-markdown";
import type { ExtraProps } from "react-markdown";
import {
  buildPoweredByUrl,
  DEFAULT_DISCLAIMER_TEXT,
} from "@/constants/disclaimer";
import { useWidgetRuntimeConfig } from "@/context/widget-runtime-config";
import { safeUrlTransform } from "@/utils/url-helpers";
import {
  breakpoints,
  colors,
  spacing,
} from "../design-tokens";
import { WizenWordmark } from "./wizen-wordmark";

type DisclaimerFooterLayout = "drawer" | "landing";

type DisclaimerFooterProps = {
  layout: DisclaimerFooterLayout;
};

const FooterRow = styled("div", {
  shouldForwardProp: prop => prop !== "layout",
})<{ layout: DisclaimerFooterLayout }>`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: ${spacing.lg}px;
  margin-top: ${({ layout }) => layout === "landing" ? spacing.sm : 0}px;
  width: 100%;
  box-sizing: border-box;

  @media (min-width: ${breakpoints.md}px) {
    flex-direction: row;
    align-items: flex-start;
    justify-content: space-between;
    gap: ${spacing["2xl"]}px;
  }
`;

const DisclaimerText = styled.div`
  color: ${colors.textSecondary};
  flex: 1 1 auto;
  min-width: 0;
  box-sizing: border-box;
`;

const DisclaimerParagraph = styled.p`
  color: ${colors.textSecondary};
  font-size: 12px;
  font-weight: 400;
  line-height: 1.4;
  margin: 0;

  &:not(:first-of-type) {
    margin-top: ${spacing.xs}px;
  }
`;

const DisclaimerUnorderedList = styled.ul`
  margin: ${spacing.xs}px 0 0 0;
  padding-left: ${spacing.lg}px;
`;

const DisclaimerOrderedList = styled.ol`
  margin: ${spacing.xs}px 0 0 0;
  padding-left: ${spacing.lg}px;
`;

const DisclaimerListItem = styled.li`
  color: ${colors.textSecondary};
  font-size: 12px;
  font-weight: 400;
  line-height: 1.4;

  &:not(:first-of-type) {
    margin-top: ${spacing.xs}px;
  }
`;

const DisclaimerLink = styled.a`
  color: ${colors.textSecondary};
  text-decoration: underline;

  &:hover,
  &:visited,
  &:active {
    color: ${colors.textSecondary};
  }
`;

const PoweredBy = styled.div`
  display: inline-flex;
  align-items: center;
  gap: ${spacing.xs}px;
  color: ${colors.textSecondary};
  font-size: 12px;
  line-height: 1.4;
  white-space: nowrap;
  flex-shrink: 0;
  align-self: flex-start;
  box-sizing: border-box;
  margin-inline-start: 0;

  @media (min-width: ${breakpoints.md}px) {
    margin-inline-start: auto;
  }
`;

const PoweredByLink = styled.a`
  color: ${colors.textSecondary};
  display: inline-flex;
  align-items: center;
  text-decoration: none;
  box-sizing: border-box;

  &:hover {
    color: ${colors.text};
  }
`;

type MarkdownLinkProps = JSX.IntrinsicElements["a"] & ExtraProps;

const DisclaimerMarkdownLink = ({
  node: _node,
  href,
  children,
  ...props
}: Readonly<MarkdownLinkProps>) => {
  if (!href) {
    return <>{children}</>;
  }

  const isExternalProtocol = href.startsWith("tel:") || href.startsWith("mailto:");

  if (isExternalProtocol) {
    return (
      <DisclaimerLink href={href} {...props}>
        {children}
      </DisclaimerLink>
    );
  }

  return (
    <DisclaimerLink href={href} target="_blank" rel="noopener noreferrer" {...props}>
      {children}
    </DisclaimerLink>
  );
};

type Components = {
  [Key in keyof JSX.IntrinsicElements]?:
    | ComponentType<JSX.IntrinsicElements[Key] & ExtraProps>
    | keyof JSX.IntrinsicElements;
};

const disclaimerMarkdownComponents: Components = {
  p: DisclaimerParagraph,
  a: DisclaimerMarkdownLink,
  ul: DisclaimerUnorderedList,
  ol: DisclaimerOrderedList,
  li: DisclaimerListItem,
};

export const DisclaimerFooter = ({ layout }: DisclaimerFooterProps) => {
  const { organizationSlug, widgetDisclaimerText } = useWidgetRuntimeConfig();
  const resolvedDisclaimerText = widgetDisclaimerText ?? DEFAULT_DISCLAIMER_TEXT;
  const poweredByHref = buildPoweredByUrl(organizationSlug);

  return (
    <FooterRow layout={layout}>
      {resolvedDisclaimerText.length > 0 && (
        <DisclaimerText>
          <ReactMarkdown
            components={disclaimerMarkdownComponents}
            urlTransform={safeUrlTransform}
          >
            {resolvedDisclaimerText}
          </ReactMarkdown>
        </DisclaimerText>
      )}
      <PoweredBy>
        Powered by
        <PoweredByLink
          href={poweredByHref}
          target="_blank"
          rel="noopener noreferrer"
        >
          <WizenWordmark />
        </PoweredByLink>
      </PoweredBy>
    </FooterRow>
  );
};
